import type { SetupState } from "@/api";

/**
 * What a business still has to do, read from the server's setup state (#1286).
 *
 * Pure and total. It decides no thresholds — the server owns those — it only names which of the
 * known items are outstanding and in what order they are worth doing.
 */

/** The complete set. Closed, so a new task cannot be introduced by a server field alone. */
export type SetupTaskKey = "signature" | "colleagues";

export interface SetupSummary {
  /** Outstanding tasks, in the order they are worth doing. Empty when nothing is left. */
  tasks: SetupTaskKey[];
  /**
   * The business cannot receive customer mail at all.
   *
   * Not a task and never in `tasks`: only the platform side can provision or repair an address, so
   * the checklist states it without offering a button. Kept separate precisely so no rendering
   * loop can accidentally turn it into something with an action.
   */
  mailWarning: boolean;
}

/** Nothing known. What the checklist renders when the server's answer cannot be trusted. */
export const NO_SETUP_SUMMARY: SetupSummary = Object.freeze({
  tasks: Object.freeze([]) as unknown as SetupTaskKey[],
  mailWarning: false,
});

/**
 * Whether a response is the shape we asked for.
 *
 * A 200 that is not this — a proxy's HTML page, a mocked `{}`, a field renamed on one side of a
 * deploy — must read as "nothing known", exactly like a rejected fetch. The cost of guessing wrong
 * is a screen telling a business its signature is set when it is not, and the reply that goes out
 * without one is the customer's problem rather than ours to notice.
 */
export function isSetupState(value: unknown): value is SetupState {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.signatureSet === "boolean" &&
    typeof candidate.hasColleagues === "boolean" &&
    typeof candidate.mailReceivable === "boolean"
  );
}

/**
 * The outstanding work, or nothing known.
 *
 * Signature before colleagues, because it is the one that silently degrades customer mail: every
 * approved reply already goes out without a legal block, whereas a business with one account still
 * works, just without cover.
 */
export function summariseSetup(value: unknown): SetupSummary {
  if (!isSetupState(value)) return NO_SETUP_SUMMARY;
  const tasks: SetupTaskKey[] = [];
  if (!value.signatureSet) tasks.push("signature");
  if (!value.hasColleagues) tasks.push("colleagues");
  return { tasks, mailWarning: !value.mailReceivable };
}

/**
 * The summary for a server response, or `null` when the response cannot be trusted.
 *
 * Keeps "nothing known" distinct from "nothing left to do" in the Getting started checklist.
 * A malformed body must never claim that the business has nothing outstanding.
 */
export function summariseSetupResponse(value: unknown): SetupSummary | null {
  return isSetupState(value) ? summariseSetup(value) : null;
}
